import React, { useCallback } from 'react';

import { AlertTriangleIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useIntegrations } from '@/hooks/useIntegrations';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { Integration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';

interface Props {
  integration?: Integration;
  disabled?: boolean;
  subtitle?: string | React.JSX.Element;
}

const DeleteFieldset = ({ integration, disabled = false, subtitle }: Props) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const integration_id = integration?.integration_id;
  const desk_id = integration?.desk_id;

  const { deleteIntegration, deleteStatus } = useIntegrations(desk_id, integration_id);

  const dispatch = useDispatch();
  const onDelete = useCallback<() => void>(() => {
    const channelName = t(getChannelKey(integration?.type));
    const deleteProps = {
      subtitle: (
        <Trans
          i18nKey="integrations.fieldsets.delete_fieldset.warn_message"
          values={{ 0: channelName }}
        />
      ),
      warning:
        integration?.type == 'whatsapp' ? (
          <Alert variant="destructive">
            <AlertTriangleIcon size={16} />
            <AlertDescription>{t('integrations.whatsapp.delete_warning')}</AlertDescription>
          </Alert>
        ) : null,
      confirm: true,
      onDelete: () => {
        deleteIntegration({ desk_id, integration_id });
      },
    };
    dispatch(pushModal(MODAL_DELETE, deleteProps));
  }, [t, dispatch, deleteIntegration, desk_id, integration_id, integration?.type]);

  return (
    <FormFieldset
      title={t('integrations.fieldsets.delete_fieldset.title')}
      subtitle={subtitle || t('integrations.fieldsets.delete_fieldset.subtitle')}
      primaryButton={{
        children: t('integrations.fieldsets.delete_fieldset.button'),
        variant: 'destructive',
        isLoading: deleteStatus === 'pending',
        disabled: disabled || !canWrite,
      }}
      onSubmit={onDelete}
      isLoading={!integration}
    />
  );
};

export default DeleteFieldset;
